import _ from 'lodash';
import { useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { CancelSaveAndContinue } from '@/core/CancelSaveAndContinue.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { Schedule } from '@/schedule/Schedule';
import { ScheduleTypeName } from '@/schedule/schedule.types';
import { getNewCronSchedule } from '@/schedule/schedule.utilities';
import { sqTimezones } from '@/utilities/datetime.constants';
import { InputGroup } from '@seeqdev/qomponents';
import type { Timezone } from '@/datetime/timezone.service';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { UpdatedSchedule } from '@/schedule/schedule.types';

export interface ScheduleFormComponentProps extends ValidatingFormComponent<any> {
  component: 'ScheduleFormComponent';
  customErrorText?: string;
  onDelete: () => void;
  schedule: string;
  customScheduleLabel: string;
  noScheduleMessage: string;
  scheduleMessage: string;
  setIsScheduleUnsaved: (isUnsaved: boolean) => void;
  isScheduleUnsaved: boolean;
  onSave?: (cronSchedule: string[] | undefined, scheduleData: UpdatedSchedule | undefined) => void;
  timezoneDefault?: Timezone;
}

export const ScheduleFormComponent = (props: ScheduleFormComponentProps) => {
  const {
    name,
    value,
    testId = 'schedule',
    validation,
    extendValidation,
    customErrorText,
    required = false,
    schedule,
    customScheduleLabel,
    noScheduleMessage,
    scheduleMessage,
    onDelete,
    setIsScheduleUnsaved,
    isScheduleUnsaved,
    onSave,
    timezoneDefault = sqTimezones.defaultTimezone,
  } = props;

  const { t } = useTranslation();

  const [updatedSchedule, setUpdatedSchedule] = useState<UpdatedSchedule>();
  const [newCronSchedule, setNewCronSchedule] = useState<string[]>();

  const formState = useForm().getState();
  const showError = _.has(formState.errors, name) && formState.submitFailed && formState.hasValidationErrors;

  const defaultValidation = (value: any) => required && !value;
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const scheduleLabel = !_.isEmpty(value) ? t(scheduleMessage, { schedule }) : t(noScheduleMessage);

  return (
    <>
      <FormFieldWrapper
        wrapperClassNames="flexFill mb20"
        testId={testId}
        showError={showError}
        errorClassNames="ml20"
        customErrorText={customErrorText}>
        <Field name={name} validate={appliedValidation}>
          {({ input, meta }) => {
            const formFieldProps = getFormFieldProps(formState, input, meta, props);
            return (
              <div>
                {!isScheduleUnsaved ? (
                  <InputGroup
                    field={
                      <div
                        data-testid={`${testId}-value`}
                        onClick={() => setIsScheduleUnsaved(true)}
                        className="width-maximum pl4 pr4">
                        {newCronSchedule ? t(customScheduleLabel) : `${scheduleLabel}`}
                      </div>
                    }
                    append={[
                      newCronSchedule || value
                        ? {
                            variant: 'button',
                            buttonProps: {
                              icon: 'fa-trash',
                              iconStyle: 'theme',
                              testId: `${testId}-delete`,
                              onClick: () => {
                                onDelete();
                                setNewCronSchedule(undefined);
                                setUpdatedSchedule(undefined);
                              },
                            },
                          }
                        : undefined,
                      {
                        variant: 'button',
                        buttonProps: {
                          icon: 'fa-pencil',
                          iconStyle: 'theme',
                          testId: `${testId}-edit`,
                          onClick: () => setIsScheduleUnsaved(true),
                        },
                      },
                    ]}
                  />
                ) : (
                  <div className="mb20 ml20 mt10">
                    <Schedule
                      cronSchedule={value}
                      initialScheduleType={ScheduleTypeName.LIVE}
                      updatedSchedule={updatedSchedule}
                      onScheduleChange={setUpdatedSchedule}
                      scheduleTypeTranslations={{ [ScheduleTypeName.LIVE]: 'SCHEDULE_TYPE.LIVE.NAME_ALTERNATE' }}
                      timezoneDefault={timezoneDefault}
                    />
                    <CancelSaveAndContinue
                      submitFn={() => {
                        const scheduleToSave = updatedSchedule ? getNewCronSchedule(updatedSchedule) : undefined;
                        if (scheduleToSave) {
                          setNewCronSchedule(scheduleToSave);
                        }
                        setIsScheduleUnsaved(false);
                        onSave?.(scheduleToSave, updatedSchedule);
                      }}
                      cancelFn={() => {
                        setUpdatedSchedule(undefined);
                        setIsScheduleUnsaved(false);
                      }}
                      values={[]}
                      submitClassNames="width-80"
                      cancelClassNames="mr10 width-80"
                    />
                  </div>
                )}
              </div>
            );
          }}
        </Field>
      </FormFieldWrapper>
    </>
  );
};
